import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useConvexAuth, useMutation } from "convex/react";
import { useRef } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { api } from "../../convex/_generated/api";
import { authClient, getActiveOrgId, LOGIN_PATH } from "../lib/authClient";
import { isChatHost, mainAppUrl } from "../lib/chatHost";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Skeleton } from "./ui/skeleton";

function SessionLoadingCard({
	title = "Checking your session...",
	description = "Clerical is validating your account and active firm selection before loading the workspace.",
}: {
	title?: string;
	description?: string;
}) {
	return (
		<section className="mx-auto flex min-h-[50vh] w-full max-w-3xl items-center px-4 py-12 sm:px-6 lg:px-8">
			<Card className="w-full shadow-sm">
				<CardHeader>
					<div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
						Workspace
					</div>
					<CardTitle>{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-3">
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-2/3" />
				</CardContent>
			</Card>
		</section>
	);
}

function NoAccessCard({
	message,
	onSignOut,
}: {
	message: string;
	onSignOut: () => void;
}) {
	return (
		<section className="mx-auto flex min-h-[50vh] w-full max-w-3xl items-center px-4 py-12 sm:px-6 lg:px-8">
			<Card className="w-full shadow-sm">
				<CardHeader>
					<div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
						No access
					</div>
					<CardTitle>Firm not provisioned</CardTitle>
					<CardDescription>{message}</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-3">
					<Button
						variant="outline"
						onClick={onSignOut}
						className="h-10 w-fit px-4"
					>
						Sign out
					</Button>
				</CardContent>
			</Card>
		</section>
	);
}

function SessionErrorCard({ message }: { message: string }) {
	return (
		<section className="mx-auto flex min-h-[50vh] w-full max-w-3xl items-center px-4 py-12 sm:px-6 lg:px-8">
			<Card className="w-full shadow-sm">
				<CardHeader>
					<div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
						Workspace
					</div>
					<CardTitle>Unable to load your workspace</CardTitle>
					<CardDescription>{message}</CardDescription>
				</CardHeader>
			</Card>
		</section>
	);
}

// No auto-redirect into /setup; users navigate there manually if they want the wizard.

export default function ProtectedRoute() {
	const location = useLocation();
	const navigate = useNavigate();
	const session = authClient.useSession();
	const { isLoading: convexLoading, isAuthenticated: convexAuthenticated } =
		useConvexAuth();

	const ensureActiveFirm = useMutation(api.firmAutoProvision.ensureActiveFirm);
	const syncOrganizationAndMember = useMutation(
		api.auth.syncOrganizationAndMember,
	);

	const activeOrgId = session.data
		? getActiveOrgId(session.data.session)
		: null;

	// Auto-provision firm when session loaded but no org active.
	// useQuery+enabled replaces a ref-guarded useEffect (runs once, caches).
	const { error: provisionError, fetchStatus: provisionFetchStatus } =
		useReactQuery({
			queryKey: ["ensure-active-firm", session.data?.user?.id],
			queryFn: async () => {
				const result = await ensureActiveFirm();
				await authClient.organization.setActive({
					organizationId: result.organizationId,
				});
				// fetchAccessToken is memoised by sessionId (unchanged by setActive), so a
				// page refresh is the only way to get a JWT with the new org claim.
				navigate(0);
				return result;
			},
			enabled: !!session.data && !activeOrgId,
			retry: false,
			staleTime: Number.POSITIVE_INFINITY,
			gcTime: Number.POSITIVE_INFINITY,
		});

	// Mirror Better Auth org into tenants/tenantMembers + kick off mailbox auto-connect.
	// Org name/slug fetched server-side, avoiding a client-side getFullOrganization call.
	const { error: syncError } = useReactQuery({
		queryKey: ["sync-org-member", activeOrgId],
		queryFn: async () => {
			await syncOrganizationAndMember({
				betterAuthOrgId: activeOrgId ?? "",
			});
			return { synced: true };
		},
		enabled: !!activeOrgId,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
	});

	// OAuth callback lands with `?ott=` for session exchange; capture at mount since
	// ConvexBetterAuthProvider strips it via history.replaceState mid-exchange.
	// Guards a brief isPending=false/data=null window that would otherwise redirect to /login.
	const arrivedWithOtt = useRef(
		new URLSearchParams(window.location.search).has("ott"),
	);
	if (session.data) arrivedWithOtt.current = false;
	const awaitingOttExchange = arrivedWithOtt.current && !session.data;

	if (session.isPending || awaitingOttExchange) return <SessionLoadingCard />;
	if (!session.data) {
		const redirectTo = `${location.pathname}${location.search}${location.hash}`;
		// chat.clerical.com doesn't host /login (keeps OAuth provider configs unchanged);
		// redirect to clerical.com/login with full chat URL, LoginPage handles cross-origin handoff.
		if (isChatHost()) {
			const fullChatUrl = `${window.location.origin}${redirectTo}`;
			window.location.replace(
				`${mainAppUrl(LOGIN_PATH)}?redirect=${encodeURIComponent(fullChatUrl)}`,
			);
			return null;
		}
		return (
			<Navigate
				to={`${LOGIN_PATH}?redirect=${encodeURIComponent(redirectTo)}`}
				replace
			/>
		);
	}

	if (provisionError) {
		const rawMsg =
			provisionError instanceof Error
				? provisionError.message
				: "Could not set up your firm. Please try again.";
		const noAccessMatch = rawMsg.match(/NO_ACCESS:\s*(.+)/);
		if (noAccessMatch) {
			return (
				<NoAccessCard
					message={noAccessMatch[1].trim()}
					onSignOut={() => {
						void authClient.signOut();
						navigate("/", { replace: true });
					}}
				/>
			);
		}
		return <SessionErrorCard message={rawMsg} />;
	}

	if (!activeOrgId || provisionFetchStatus === "fetching") {
		return (
			<SessionLoadingCard
				title="Setting up your firm..."
				description="We're creating your Clerical workspace based on your email domain."
			/>
		);
	}

	if (convexLoading || !convexAuthenticated) return <SessionLoadingCard />;

	if (syncError) {
		const msg =
			syncError instanceof Error
				? syncError.message
				: "Could not sync your firm. Please refresh and try again.";
		return <SessionErrorCard message={msg} />;
	}

	return <Outlet />;
}
